/**
 * What a layout block puts on the printed label, line by line.
 *
 * The shape lives in `shared` because two renderers have to agree on it: the
 * PDF/raster painter on the server and the editor canvas in the browser. The
 * editor used to receive one flat string per block — the nutrition table
 * arrived as "Energia: 570 kJ / 136 kcal · Grassi: 6,8 g · …" and was laid out
 * as a paragraph while the printer set it as a column of figures. An operator
 * sizing a block on that preview was sizing it on a layout the label never has.
 *
 * Two shapes, because two legal requirements cannot be met by a single string:
 *
 * - `FLOW` wraps and can mix weights inside one line, which is what art. 21
 *   §1 lett. b) Reg. (UE) 1169/2011 asks for — the allergen set apart inside
 *   the ingredient list, not beside it.
 * - `ROW` is a nutrient and its figure, so art. 34 §2 can align the figures in
 *   a column instead of running them together in a sentence.
 */

/** A stretch of text and whether it is set apart from the text around it. */
export interface PrepackagedTextRun {
  text: string;
  bold: boolean;
}

/** A small pictogram painted INLINE at the left of a FLOW line's first row —
 *  the packaging material and family symbols share the row with their text
 *  instead of stacking into a tall block. */
export interface PrepackagedLeadingImage {
  imageId: string;
  heightMm: number;
  aspectRatio: number;
}

export type PrepackagedBlockLine =
  | {
      kind: 'FLOW';
      runs: PrepackagedTextRun[];
      secondary?: boolean;
      /** Pictograms painted in a column left of the text, at least as tall as
       *  the row: the block keeps one band per line instead of one band per
       *  pictogram. */
      leadingImages?: PrepackagedLeadingImage[];
    }
  | {
      kind: 'ROW';
      label: string;
      value: string;
      indented: boolean;
      /**
       * Emphasis convention, not law: the mandatory nutrients (art. 30 §1)
       * are set apart from the voluntary "di cui" detail and micronutrients
       * by a bold label. The figure stays regular — only the name is bold.
       */
      bold?: boolean;
      /**
       * Art. 34 §2 lett. c Reg. (UE) 1169/2011: the same nutrient expressed
       * per portion, printed in a second figure column. Present only when
       * the operator asked for the per-portion declaration and the recipe
       * carries a servings count; macro rows alone, never micronutrients.
       */
      portionValue?: string;
    }
  /**
   * The column captions of a nutrient table that carries a per-portion
   * column (art. 34 §2 lett. c / All. XV pt 2): an empty label cell, the
   * per-100 g caption over the first figure column, and the portion caption
   * — with the portion quantified — over the second. Only ever emitted as
   * the first line of a table run.
   */
  | { kind: 'ROW_HEADER'; valueCaption: string; portionCaption: string }
  /**
   * A constant system image. `imageId` is a key into the render catalogue, never a
   * path or bytes — the artwork is embedded server-side from
   * `backend/assets/organic/`, exactly as the tenant logo already is kept
   * out of the canonical payload.
   */
  | { kind: 'IMAGE'; imageId: string; heightMm: number; aspectRatio: number };

/** Millimetres a "di cui" nutrient is pushed in under its parent. */
export const PREPACKAGED_ROW_INDENT_MM = 2;

/**
 * Art. 34 §2 Reg. (UE) 1169/2011: consecutive nutrient rows print as a ruled
 * table. One hairline between two rows, this thick, in black.
 */
export const NUTRITION_TABLE_RULE_MM = 0.2;

/** Breathing room kept between a row's glyphs and the hairline under them. */
export const NUTRITION_TABLE_RULE_GAP_MM = 0.15;

/**
 * Breathing room between the widest label and the column divider, as a
 * fraction of the font size. The divider is one straight vertical per table
 * — after the widest label of ALL its rows — so both renderers pad it the
 * same way or the canvas and the print disagree on the column edge.
 */
export const NUTRITION_TABLE_DIVIDER_PAD_RATIO = 0.3;

/**
 * Air between the text and the box edges, in millimetres: this much inside
 * each side edge (labels and figures never touch the verticals) and this
 * much above and below every row's glyphs (text never sits on a hairline).
 * The painter charges it to width/height, the content floor demands the
 * same, and the canvas pads by it — three derivations would let the
 * operator shrink a box under a table the printer then refuses to print.
 */
export const NUTRITION_TABLE_CELL_PAD_MM = 0.5;
export const NUTRITION_TABLE_ROW_PAD_MM = 0.25;

/** Extra height the per-row pads add over `rowLineCount` rows of text. */
export function nutritionTableRowPadsExtraHeightMm(rowLineCount: number): number {
  return rowLineCount > 1 ? rowLineCount * 2 * NUTRITION_TABLE_ROW_PAD_MM : 0;
}

/**
 * The height a ruled nutrient table adds over its rows of text: one boundary
 * per pair of adjacent rows, gap plus hairline. The PDF painter charges this
 * to the height it reports and `blockLinesContentFloorMm` charges the same —
 * two derivations of it would let the operator shrink a box under a table
 * the printer then refuses to print.
 */
export function nutritionTableRulesExtraHeightMm(rowLineCount: number): number {
  return rowLineCount > 1
    ? (rowLineCount - 1) * (NUTRITION_TABLE_RULE_MM + NUTRITION_TABLE_RULE_GAP_MM)
    : 0;
}

/** The legal denomination is set below the fancy name, not shouted with it. */
export const PREPACKAGED_SECONDARY_LINE_RATIO = 0.72;

/** Spaces standing in for `PREPACKAGED_ROW_INDENT_MM` when the lines are
 *  measured as plain text: the indent is part of what the row occupies. */
const ROW_INDENT_SPACES = '  ';

/**
 * The lines as plain text, one printed line per text line — what a measurer
 * that only takes a string (the editor's content floor) has to see. Joining a
 * nutrient table onto one line told the floor the block needed one row of text
 * when the printer sets one row per nutrient.
 */
export function prepackagedBlockLinesToText(lines: readonly PrepackagedBlockLine[]): string {
  return lines
    .map((line) => {
      if (line.kind === 'ROW') {
        return `${line.indented ? ROW_INDENT_SPACES : ''}${line.label}: ${line.value}${
          line.portionValue === undefined ? '' : ` / ${line.portionValue}`
        }`;
      }
      if (line.kind === 'ROW_HEADER') {
        // The header prints two captions over two figure columns; as plain
        // text they are one measurement row like any other.
        return `: ${line.valueCaption} / ${line.portionCaption}`;
      }
      // An IMAGE line prints no text at all: counting it as a text line would
      // tell the measurer the block needs a row of glyphs it never sets.
      return line.kind === 'IMAGE' ? '' : line.runs.map((run) => run.text).join('');
    })
    .join('\n');
}

/**
 * A plain string as block lines: every hard newline is a line the label keeps
 * (the legal denomination under the fancy name), so the renderers can take one
 * shape whether the text was composed on the server or typed in the wizard.
 */
export function prepackagedTextToBlockLines(text: string): PrepackagedBlockLine[] {
  if (text.length === 0) {
    return [];
  }
  return text
    .split(/\r?\n/)
    .map((line): PrepackagedBlockLine => ({ kind: 'FLOW', runs: [{ text: line, bold: false }] }));
}
